import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import AstraModel from './AstraModel';

// Camera path waypoints (scene 1 -> 4)  — android is ~1.85m tall, head ~1.75m, feet at 0
const WAYPOINTS = [
  // Scene 1 — Hero: close face/shoulder portrait (head tracking mouse shines here)
  { pos: [0.0, 1.72, 0.85], look: [0, 1.72, 0] },
  // Scene 2 — Neural Core: 3/4 back, data particles in foreground
  { pos: [-1.5, 1.85, -1.2], look: [0, 1.55, 0] },
  // Scene 3 — Chassis / Experience: side orbit lower on torso/arms
  { pos: [1.9, 1.2, 1.0], look: [0, 1.0, 0] },
  // Scene 4 — Archive: wide hangar shot showing full android
  { pos: [0.0, 1.3, 5.2], look: [0, 0.95, 0] },
];

const lerpArr = (a, b, t) => [
  THREE.MathUtils.lerp(a[0], b[0], t),
  THREE.MathUtils.lerp(a[1], b[1], t),
  THREE.MathUtils.lerp(a[2], b[2], t),
];

function sampleCamera(progress) {
  const total = WAYPOINTS.length - 1;
  const scaled = THREE.MathUtils.clamp(progress, 0, 1) * total;
  const i = Math.floor(scaled);
  const f = scaled - i;
  const a = WAYPOINTS[i];
  const b = WAYPOINTS[Math.min(i + 1, total)];
  const t = f * f * (3 - 2 * f);
  return { pos: lerpArr(a.pos, b.pos, t), look: lerpArr(a.look, b.look, t) };
}

function CameraRig({ scrollRef, mouseRef }) {
  const { camera } = useThree();
  const lookTarget = useRef(new THREE.Vector3(0, 1.5, 0));

  useFrame((_, delta) => {
    const p = scrollRef.current || 0;
    const { pos, look } = sampleCamera(p);
    const mx = (mouseRef.current?.x || 0) * 0.15;
    const my = (mouseRef.current?.y || 0) * 0.08;
    const desired = new THREE.Vector3(pos[0] + mx, pos[1] + my, pos[2]);
    camera.position.lerp(desired, 1 - Math.pow(0.001, delta));
    lookTarget.current.lerp(
      new THREE.Vector3(look[0], look[1], look[2]),
      1 - Math.pow(0.001, delta)
    );
    camera.lookAt(lookTarget.current);
  });
  return null;
}

// Particles built programmatically (not JSX <bufferAttribute>)
function Particles({ count = 260 }) {
  const ref = useRef();
  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 0] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 1] = Math.random() * 4;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    return g;
  }, [count]);

  const mat = useMemo(
    () =>
      new THREE.PointsMaterial({
        size: 0.02,
        color: new THREE.Color('#22d3ee'),
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
      }),
    []
  );

  useFrame((state) => {
    if (ref.current) ref.current.rotation.y = state.clock.elapsedTime * 0.02;
  });

  return <points ref={ref} geometry={geom} material={mat} />;
}

// Soft floor plane for grounding (subtle reflection look)
function Floor() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <circleGeometry args={[6, 48]} />
      <meshStandardMaterial
        color={'#040a12'}
        metalness={0.6}
        roughness={0.55}
      />
    </mesh>
  );
}

export default function AstraScene() {
  const scrollRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      scrollRef.current = max > 0 ? h.scrollTop / max : 0;
    };
    const onMouse = (e) => {
      mouseRef.current = {
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -((e.clientY / window.innerHeight) * 2 - 1),
      };
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse);
    onScroll();
    const t = setTimeout(() => setReady(true), 100);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
      clearTimeout(t);
    };
  }, []);

  const mouseProxy = useMemo(
    () => ({
      get x() { return mouseRef.current.x; },
      get y() { return mouseRef.current.y; },
    }),
    []
  );

  return (
    <div className="canvas-fixed">
      <Canvas
        shadows
        dpr={[1, 1.75]}
        gl={{ antialias: true, powerPreference: 'high-performance' }}
        camera={{ position: [0, 1.72, 0.85], fov: 28, near: 0.05, far: 100 }}
        onCreated={({ scene, gl }) => {
          scene.background = new THREE.Color(0x03060b);
          scene.fog = new THREE.Fog(0x03060b, 4, 18);
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.05;
        }}
      >
        {/* Cinematic lighting */}
        <ambientLight intensity={0.2} color={'#99ddee'} />
        <directionalLight
          position={[2.5, 4, 2]}
          intensity={1.4}
          color={'#eafcff'}
          castShadow
        />
        <spotLight
          position={[0, 3.2, 2.2]}
          angle={0.55}
          penumbra={0.85}
          intensity={2.4}
          color={'#bfefff'}
          castShadow
        />
        <pointLight position={[-2.5, 1.5, -2]} intensity={2.0} color={'#22d3ee'} distance={8} />
        <pointLight position={[2.5, 1.8, -2.5]} intensity={1.2} color={'#0ea5b7'} distance={8} />
        <pointLight position={[0, 0.4, 2.5]} intensity={0.6} color={'#7dd3fc'} distance={6} />

        <Suspense fallback={null}>
          <AstraModel mouse={mouseProxy} position={[0, 0, 0]} scale={1} />
          <Floor />
          <ContactShadows
            position={[0, 0.005, 0]}
            opacity={0.55}
            scale={9}
            blur={2.4}
            far={4}
            color={'#001821'}
          />
          <Particles count={260} />
        </Suspense>

        {ready && <CameraRig scrollRef={scrollRef} mouseRef={mouseRef} />}
      </Canvas>
    </div>
  );
}
